/*
  Franky wishlist integration for Swish (Wishlist King).

  - <wishlist-heart>: heart toggle on product cards and the product page
  - <wishlist-count>: header heart with live item counter
  - Tracks "Shared Wishlist" in Klaviyo when a customer shares their wishlist
*/
(() => {
  const READY_TIMEOUT = 20000;
  let readyPromise;

  // Swish loads its SDK lazily and exposes it as window.WishlistKing
  const whenWishlistReady = () => {
    if (readyPromise) return readyPromise;

    readyPromise = new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const app = window.WishlistKing;
        if (app && app.state) return resolve(app);
        if (Date.now() - started > READY_TIMEOUT) return reject(new Error('Swish not available'));
        setTimeout(check, 150);
      };
      check();
    });

    return readyPromise;
  };

  class WishlistHeart extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      whenWishlistReady()
        .then((app) => {
          this.app = app;
          this.observe();
        })
        .catch(() => this.setAttribute('hidden', ''));
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.subscription?.unsubscribe();
    }

    get productHandle() {
      return this.dataset.productHandle;
    }

    get variantId() {
      return this.dataset.variantId ? parseInt(this.dataset.variantId, 10) : undefined;
    }

    observe() {
      this.subscription?.unsubscribe();
      this.subscription = this.app.state
        .observeProductInfo({ productHandle: this.productHandle, variantId: this.variantId })
        .subscribe((productInfo) => {
          this.productInfo = productInfo;
          this.setSaved(productInfo.inWishlist);
        });
    }

    setSaved(saved) {
      this.toggleAttribute('saved', saved);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    }

    async onClick(event) {
      // Hearts on cards sit inside/near product links
      event.preventDefault();
      event.stopPropagation();

      if (!this.app || this.hasAttribute('pending')) return;

      const wasSaved = !!this.productInfo?.inWishlist;
      this.setAttribute('pending', '');
      this.setSaved(!wasSaved);

      try {
        if (wasSaved && this.productInfo.wishlistItemId) {
          await this.app.removeWishlistItem({ wishlistItemId: this.productInfo.wishlistItemId });
        } else {
          await this.app.addWishlistItem({ productHandle: this.productHandle, variantId: this.variantId });
        }
      } catch (error) {
        console.error(error);
        this.setSaved(wasSaved);
      } finally {
        this.removeAttribute('pending');
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.counter = this.querySelector('[data-wishlist-count]');

      whenWishlistReady()
        .then((app) => {
          this.subscription = app.state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => {
            const count = wishlist?.numItems || 0;
            if (!this.counter) return;
            this.counter.textContent = count;
            this.counter.dataset.count = count;
          });
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      this.subscription?.unsubscribe();
    }
  }

  if (!customElements.get('wishlist-heart')) customElements.define('wishlist-heart', WishlistHeart);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);

  // Share tracking for the "share your wishlist" campaign (Klaviyo segment: Shared Wishlist >= 5 times)
  document.addEventListener('wk:wishlist:share:success', (event) => {
    const data = event.detail?.data || {};
    const wishlist = data.wishlist;

    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push([
      'track',
      'Shared Wishlist',
      {
        WishlistUrl: window.location.href,
        WishlistItemCount: wishlist?.numItems ?? null,
      },
    ]);
  });
})();
